import Tick from '@assets/icons/tick.svg'
import Warning from '@assets/icons/warning.svg'
import { useFormContext } from 'react-hook-form'
import { twMerge } from 'tailwind-merge'

type Options = {
  label: string
  value: string
}

type Props = {
  name: string
  label: string
  options: Array<Options>
  helper?: string
  optionsLabel?: string
  optional?: boolean
  isChild?: boolean
  infoText?: string
  extraOnChange?: (e: any) => void
}

const SelectInput = ({
  name,
  label,
  options,
  helper,
  optionsLabel,
  isChild,
  optional,
  extraOnChange,
  ...rest
}: Props) => {
  const { register, getFieldState, formState } = useFormContext()
  const { error, isTouched, isDirty } = getFieldState(name, formState)
  const { onChange, onBlur, name: registeredName, ref } = register(name)

  return (
    <div
      className={
        isChild
          ? `items-start`
          : `grid grid-cols-1 md:grid-cols-3 lg:grid-cols-2 md:gap-4 items-start justify-top mb-4 md:mb-2`
      }
      data-gt-name="componentevent"
    >
      <div className="md:col-span-2 lg:col-span-1 text-base-text">
        <label className="block" htmlFor={name}>
          {label && (
            <span className="inline-block text-base-text mb-1">
              {label}{' '}
              {optional && (
                <span className="text-xs lowercase whitespace-nowrap">
                  (optional)
                </span>
              )}
            </span>
          )}
          <div className="relative">
            <select
              id={name}
              className={twMerge(
                `rounded-input block border-input-border w-full p-[0.7rem] pr-16`,
                error && 'border-red',
                !error && isTouched && isDirty && 'border-green',
              )}
              aria-invalid={error ? 'true' : 'false'}
              data-gt-action="select-input-interaction"
              data-gt-label={name}
              onChange={(e) => {
                onChange(e)
                if (extraOnChange) extraOnChange(e)
              }}
              onBlur={onBlur}
              name={registeredName}
              ref={ref}
              {...rest}
            >
              <option value="">{optionsLabel || 'Please select'}</option>
              {options.map((item) => (
                <option key={item.value} value={item.value}>
                  {item.label}
                </option>
              ))}
            </select>
            {error && (
              <Warning className="absolute inset-y-0 right-10 top-4 h-4 pointer-events-none text-red" />
            )}
            {!error && isTouched && isDirty && (
              <Tick className="absolute inset-y-0 right-10 top-4 h-4 pointer-events-none text-green" />
            )}
          </div>
        </label>
        {error && (
          <p className="text-red text-tiny mt-1" role="alert">
            {error.message}
          </p>
        )}
      </div>
      {!isChild && (
        <div className="mt-1 md:mt-8 flex items-center md:min-h-[50px]">
          {helper && <p className="text-tiny leading-5">{helper}</p>}
        </div>
      )}
    </div>
  )
}

export default SelectInput
